import { effectiveCells } from "../ship";
import type { Ship, ShipCollider, ShipModule } from "../types";
import { getSpriteMask } from "./collision-shapes";

export type { ShipCollider } from "../types";

/** Padding (px) around bbox; max query dist = PAD_PX × metersPerPixel. */
const PAD_PX = 12;

/** Cap matters only for grazing rays (MIN_STEP_PX guarantees progress). */
const MAX_ITER = 48;

/** ~half a pixel; below this the trace is touching the hull. */
const HIT_EPS_M = 0.25;

/** Guards grazing-ray pathology (d → 0 without crossing HIT_EPS). */
const MIN_STEP_PX = 0.5;

export function buildShipCollider(ship: Ship): ShipCollider | null {
  const cellSize = ship.cellSizeM;
  const metersPerPixel = cellSize / 8;

  let minGx = Infinity, minGy = Infinity, maxGx = -Infinity, maxGy = -Infinity;
  for (const m of ship.modules) {
    if (m.hp <= 0 || m.illegal || m.def.noCollision) continue;
    const def = m.def;
    const eff = effectiveCells(def, m.facing);
    if (m.gx < minGx) minGx = m.gx;
    if (m.gy < minGy) minGy = m.gy;
    if (m.gx + eff.w > maxGx) maxGx = m.gx + eff.w;
    if (m.gy + eff.h > maxGy) maxGy = m.gy + eff.h;
  }
  if (!isFinite(minGx)) return null;

  const innerW = (maxGx - minGx) * 8;
  const innerH = (maxGy - minGy) * 8;
  const widthPx = innerW + 2 * PAD_PX;
  const heightPx = innerH + 2 * PAD_PX;

  const originX = minGx * cellSize - ship.com.x - PAD_PX * metersPerPixel;
  const originY = minGy * cellSize - ship.com.y - PAD_PX * metersPerPixel;

  const occupancy = new Uint8Array(widthPx * heightPx);
  const owners: (ShipModule | null)[] = new Array(widthPx * heightPx).fill(null);
  for (const m of ship.modules) {
    if (m.hp <= 0 || m.illegal || m.def.noCollision) continue;
    const def = m.def;
    const eff = effectiveCells(def, m.facing);
    const mask = getSpriteMask(def.sprite);
    const moduleOriginPxX = PAD_PX + (m.gx - minGx) * 8;
    const moduleOriginPxY = PAD_PX + (m.gy - minGy) * 8;
    const modWPx = eff.w * 8;
    const modHPx = eff.h * 8;
    if (!mask) {
      // No cached mask = fully-opaque trivial sprite; treat as solid.
      for (let dy = 0; dy < modHPx; dy++) {
        const row = (moduleOriginPxY + dy) * widthPx + moduleOriginPxX;
        for (let dx = 0; dx < modWPx; dx++) {
          occupancy[row + dx] = 1;
          owners[row + dx] = m;
        }
      }
      continue;
    }
    blitMaskRotated(occupancy, owners, m, mask.data, mask.w, mask.h,
                    moduleOriginPxX, moduleOriginPxY, modWPx, modHPx,
                    m.facing, widthPx);
  }

  const sdf = signedDistanceTransform(occupancy, widthPx, heightPx, metersPerPixel);
  return { sdf, widthPx, heightPx, metersPerPixel, originX, originY, owners };
}

function blitMaskRotated(
  occupancy: Uint8Array, owners: (ShipModule | null)[],
  m: ShipModule,
  mask: Uint8Array, mw: number, mh: number,
  dstX: number, dstY: number, dstW: number, dstH: number,
  facing: 0 | 1 | 2 | 3,
  stride: number,
): void {
  for (let dy = 0; dy < dstH; dy++) {
    for (let dx = 0; dx < dstW; dx++) {
      let sx: number, sy: number;
      switch (facing) {
        case 0: sx = dx;          sy = dy;          break;
        case 1: sx = dy;          sy = dstW - 1 - dx; break;
        case 2: sx = dstW - 1 - dx; sy = dstH - 1 - dy; break;
        default: sx = dstH - 1 - dy; sy = dx;        break;
      }
      if (sx < 0 || sx >= mw || sy < 0 || sy >= mh) continue;
      if (!mask[sy * mw + sx]) continue;
      const idx = (dstY + dy) * stride + (dstX + dx);
      occupancy[idx] = 1;
      owners[idx] = m;
    }
  }
}

function signedDistanceTransform(
  occ: Uint8Array, w: number, h: number, mpp: number,
): Float32Array {
  const distOut = new Float32Array(w * h);
  const distIn = new Float32Array(w * h);
  edt2d(occ, distOut, w, h, false);
  edt2d(occ, distIn, w, h, true);
  const sdf = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const dOut = Math.sqrt(distOut[i]) * mpp;
    const dIn = Math.sqrt(distIn[i]) * mpp;
    sdf[i] = dOut - dIn;
  }
  return sdf;
}

const INF = 1e20;

function edt2d(
  src: Uint8Array, dst: Float32Array,
  w: number, h: number, invert: boolean,
): void {
  const tmp = new Float32Array(w * h);
  const f = new Float32Array(Math.max(w, h));
  const d = new Float32Array(Math.max(w, h));
  const v = new Int32Array(Math.max(w, h));
  const z = new Float32Array(Math.max(w, h) + 1);

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const isFeature = invert ? !src[y * w + x] : !!src[y * w + x];
      f[x] = isFeature ? 0 : INF;
    }
    edt1d(f, d, v, z, w);
    for (let x = 0; x < w; x++) tmp[y * w + x] = d[x];
  }
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = tmp[y * w + x];
    edt1d(f, d, v, z, h);
    for (let y = 0; y < h; y++) dst[y * w + x] = d[y];
  }
}

function edt1d(
  f: Float32Array, d: Float32Array,
  v: Int32Array, z: Float32Array, n: number,
): void {
  let k = 0;
  v[0] = 0;
  z[0] = -INF;
  z[1] = INF;
  for (let q = 1; q < n; q++) {
    let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    while (s <= z[k]) {
      k--;
      s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    }
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = INF;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    const dx = q - v[k];
    d[q] = dx * dx + f[v[k]];
  }
}

interface SphereTraceHit {
  /** Fraction of the swept segment where surface was crossed. */
  t: number;
  /** Hit point (ship-local meters). */
  lx: number;
  ly: number;
  /** Outward surface normal (ship-local). */
  nx: number;
  ny: number;
  /** Module that gets the damage credit. */
  owner: ShipModule | null;
}

/** Sphere-trace swept point against SDF; coords are ship-local meters. */
export function sphereTrace(
  c: ShipCollider,
  x0: number, y0: number,
  x1: number, y1: number,
): SphereTraceHit | null {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const len = Math.hypot(dx, dy);
  // Zero-motion: discrete inside test only.
  if (len < 1e-9) {
    const d0 = sampleSdf(c, x0, y0);
    if (d0 < HIT_EPS_M) return makeHit(c, x0, y0, 0);
    return null;
  }
  const ndx = dx / len;
  const ndy = dy / len;
  const minStep = MIN_STEP_PX * c.metersPerPixel;

  const dStart = sampleSdf(c, x0, y0);
  if (dStart < HIT_EPS_M) return makeHit(c, x0, y0, 0);

  let traveled = 0;
  for (let iter = 0; iter < MAX_ITER; iter++) {
    const px = x0 + ndx * traveled;
    const py = y0 + ndy * traveled;
    const d = sampleSdf(c, px, py);
    if (d < HIT_EPS_M) {
      return makeHit(c, px, py, traveled / len);
    }
    traveled += d > minStep ? d : minStep;
    if (traveled > len) return null;
  }
  return null;
}

function makeHit(c: ShipCollider, lx: number, ly: number, t: number): SphereTraceHit {
  const g = sdfGradient(c, lx, ly);
  // Step inward along −gradient so the owner lookup lands on a solid pixel.
  const stepInM = c.metersPerPixel * 2;
  const ownerLx = lx - g.x * stepInM;
  const ownerLy = ly - g.y * stepInM;
  const px = (ownerLx - c.originX) / c.metersPerPixel;
  const py = (ownerLy - c.originY) / c.metersPerPixel;
  const ix = px < 0 ? 0 : px >= c.widthPx ? c.widthPx - 1 : Math.floor(px);
  const iy = py < 0 ? 0 : py >= c.heightPx ? c.heightPx - 1 : Math.floor(py);
  const owner = c.owners[iy * c.widthPx + ix];
  return { t, lx, ly, nx: g.x, ny: g.y, owner };
}

/** Bilinear SDF sample (ship-local m); outside-grid: edge dist + linear falloff. */
function sampleSdf(c: ShipCollider, x: number, y: number): number {
  const px = (x - c.originX) / c.metersPerPixel - 0.5;
  const py = (y - c.originY) / c.metersPerPixel - 0.5;
  const w = c.widthPx;
  const h = c.heightPx;
  if (px <= 0 || py <= 0 || px >= w - 1 || py >= h - 1) {
    const cx = px < 0 ? 0 : px > w - 1 ? w - 1 : px;
    const cy = py < 0 ? 0 : py > h - 1 ? h - 1 : py;
    const ix = Math.floor(cx);
    const iy = Math.floor(cy);
    const edge = c.sdf[iy * w + ix];
    const dxOff = px - cx;
    const dyOff = py - cy;
    return edge + Math.hypot(dxOff, dyOff) * c.metersPerPixel;
  }
  const ix = Math.floor(px);
  const iy = Math.floor(py);
  const fx = px - ix;
  const fy = py - iy;
  const i00 = iy * w + ix;
  const d00 = c.sdf[i00];
  const d10 = c.sdf[i00 + 1];
  const d01 = c.sdf[i00 + w];
  const d11 = c.sdf[i00 + w + 1];
  const a = d00 + (d10 - d00) * fx;
  const b = d01 + (d11 - d01) * fx;
  return a + (b - a) * fy;
}

function sdfGradient(c: ShipCollider, x: number, y: number): { x: number; y: number } {
  const h = c.metersPerPixel;
  const dxN = sampleSdf(c, x + h, y) - sampleSdf(c, x - h, y);
  const dyN = sampleSdf(c, x, y + h) - sampleSdf(c, x, y - h);
  const len = Math.hypot(dxN, dyN);
  if (len < 1e-9) return { x: 1, y: 0 };
  return { x: dxN / len, y: dyN / len };
}
